/**
 * 播放器控制器
 * 提供全局播放器控制接口，支持在浏览器 console 中直接操作
 *
 * 使用示例（在浏览器 console 中）：
 * - await player.pause() - 暂停播放，返回操作结果
 * - await player.play() - 开始播放，返回操作结果
 * - await player.stop() - 停止播放，返回操作结果
 * - player.seek(60) - 跳转到 60 秒
 * - await player.setQuality('720P') - 切换清晰度，返回操作结果
 * - await player.setSpeed('1.5x') - 切换倍速，返回操作结果
 *
 * 操作结果格式: { success: boolean, message?: string, error?: Error }
 */

import { usePlayerStore, type OperationResult } from '@/stores/playerStore';
import { logger } from '@/utils/devUtils';

/**
 * 播放器控制器类
 * 封装播放器操作，提供简洁的 API
 * 所有异步方法返回 Promise<OperationResult>
 */
class PlayerController {
  /**
   * 播放
   * @returns Promise<OperationResult> 操作结果
   */
  play(): Promise<OperationResult> {
    return usePlayerStore.getState().play();
  }

  /**
   * 暂停
   * @returns Promise<OperationResult> 操作结果
   */
  pause(): Promise<OperationResult> {
    return usePlayerStore.getState().pause();
  }

  /**
   * 切换播放/暂停
   * @returns Promise<OperationResult> 操作结果
   */
  toggle(): Promise<OperationResult> {
    return usePlayerStore.getState().togglePlayPause();
  }

  /**
   * 停止播放
   * @returns Promise<OperationResult> 操作结果
   */
  stop(): Promise<OperationResult> {
    return usePlayerStore.getState().stop();
  }

  /**
   * 跳转到指定时间（秒）
   */
  seek(time: number): void {
    usePlayerStore.getState().seek(time);
  }

  /**
   * 快进（秒）
   */
  forward(seconds = 10): void {
    usePlayerStore.getState().seekRelative(seconds);
  }

  /**
   * 快退（秒）
   */
  rewind(seconds = 10): void {
    usePlayerStore.getState().seekRelative(-seconds);
  }

  /**
   * 下一集
   */
  next(): void {
    usePlayerStore.getState().playNext();
  }

  /**
   * 上一集
   */
  previous(): void {
    usePlayerStore.getState().playPrevious();
  }

  /**
   * 获取当前状态
   */
  getState() {
    return usePlayerStore.getState();
  }

  /**
   * 获取当前播放时间
   */
  getCurrentTime(): number {
    return usePlayerStore.getState().currentTime;
  }

  /**
   * 获取总时长
   */
  getTotalTime(): number {
    return usePlayerStore.getState().totalTime;
  }

  /**
   * 获取播放进度（0-100）
   */
  getProgress(): number {
    return usePlayerStore.getState().getProgress();
  }

  /**
   * 设置清晰度
   * @returns Promise<OperationResult> 操作结果
   */
  setQuality(quality: 'auto' | '480P' | '720P' | '1080P' | '4K'): Promise<OperationResult> {
    return usePlayerStore.getState().setQuality({ quality });
  }

  /**
   * 设置播放倍速
   * @returns Promise<OperationResult> 操作结果
   */
  setSpeed(speed: '0.5x' | '0.75x' | '1x' | '1.25x' | '1.5x' | '2x'): Promise<OperationResult> {
    return usePlayerStore.getState().setSpeed(speed);
  }

  /**
   * 设置字幕开关
   * @returns Promise<OperationResult> 操作结果
   */
  setSubtitleEnabled(enabled: boolean): Promise<OperationResult> {
    return usePlayerStore.getState().setSubtitleEnabled(enabled);
  }

  /**
   * 设置字幕语言（通过语言代码）
   * @param lang 语言代码
   * @returns Promise<OperationResult> 操作结果
   */
  async setSubtitleLanguage(lang: 'en' | 'zh' | 'ja' | 'ko' | 'off'): Promise<OperationResult> {
    const state = usePlayerStore.getState();
    const { subtitleList } = state;

    // 将语言代码转换为字幕索引
    let index = 0;
    if (lang === 'off') {
      // "无字幕"通常在索引 0
      index = 0;
    } else {
      // 查找匹配的字幕索引
      const langMap: Record<string, string[]> = {
        en: ['EN', 'English'],
        zh: ['ZH-CN', 'ZH', '简体中文', '繁体中文'],
        ja: ['JA', 'Japanese', '日本語'],
        ko: ['KO', 'Korean', '한국어'],
      };

      const matchLangs = langMap[lang] || [];
      index = subtitleList.findIndex((item) =>
        matchLangs.some((l) => item.lang.toUpperCase().includes(l.toUpperCase()))
      );

      if (index === -1) {
        index = 0; // 未找到，默认第一个
      }
    }

    return state.setSubtitleLanguage(index);
  }

  /**
   * 设置字幕大小
   * @param size 字幕大小（支持百分比形式：'60%' | '80%' | '100%' | '120%' | '130%'）
   * @returns Promise<OperationResult> 操作结果
   */
  setSubtitleSize(size: '60%' | '80%' | '100%' | '120%' | '130%'): Promise<OperationResult> {
    // 将百分比映射到 SubtitleSize 类型
    const sizeMap: Record<string, 'small' | 'middle' | 'large' | 'larger' | 'largest'> = {
      '60%': 'small',
      '80%': 'middle',
      '100%': 'large',
      '120%': 'larger',
      '130%': 'largest',
    };

    return usePlayerStore.getState().setSubtitleSize(sizeMap[size]);
  }

  /**
   * 设置音频语言
   * @returns Promise<OperationResult> 操作结果
   */
  setAudioLanguage(lang: 'en' | 'zh' | 'ja' | 'ko'): Promise<OperationResult> {
    return usePlayerStore.getState().setAudioLanguage(lang);
  }

  /**
   * 设置跳过片头片尾
   * @param skipEnd 片尾开始时间点（秒）
   * @param skipHeadBegin 片头开始时间（秒）
   * @param skipStart 片头结束时间点（秒）
   * @param skipTailEnd 片尾结束时间（秒）
   */
  setSkipIntro(skipEnd: number, skipHeadBegin: number, skipStart: number, skipTailEnd: number): void {
    usePlayerStore.getState().setSkipIntro(skipEnd, skipHeadBegin, skipStart, skipTailEnd);
  }

  /**
   * 格式化时间显示
   */
  formatTime(seconds: number): string {
    return usePlayerStore.getState().formatTime(seconds);
  }

  /**
   * 获取播放器内核配置
   */
  getPlayerKernel(): 'hls.js' | 'origin' {
    return usePlayerStore.getState().playerKernel;
  }

  /**
   * 设置播放器内核配置
   */
  setPlayerKernel(kernel: 'hls.js' | 'origin'): void {
    usePlayerStore.getState().setPlayerKernel(kernel);
  }
}

// 创建全局控制器实例
const playerController = new PlayerController();

/**
 * 初始化全局播放器控制器
 * 将控制器暴露到 window 对象，方便在 console 中使用
 */
export function initPlayerController(): void {
  if (typeof window !== 'undefined') {
    // 暴露控制器到全局
    (window as any).player = playerController;

    // 也暴露 store，方便直接访问状态
    (window as any).playerStore = usePlayerStore;

    // 暴露原始播放器实例的 getter（通过 store 获取）
    Object.defineProperty((window as any).player, 'instance', {
      get: () => usePlayerStore.getState().playerInstance,
      enumerable: true,
      configurable: true,
    });

    // 打印使用说明
    if (process.env.NODE_ENV === 'development') {
      logger.log('%c🎮 播放器控制器已初始化', 'color: #00c8c8; font-weight: bold; font-size: 14px;');
      logger.log('%c使用 player 对象控制播放器 (支持 async/await):', 'color: #00c8c8; font-weight: bold;');
      logger.log('  await player.play() - 播放 (返回操作结果)');
      logger.log('  await player.pause() - 暂停 (返回操作结果)');
      logger.log('  await player.stop() - 停止播放 (返回操作结果)');
      logger.log('  await player.toggle() - 切换播放/暂停 (返回操作结果)');
      logger.log('  player.seek(60) - 跳转到 60 秒');
      logger.log('  player.forward(10) - 快进 10 秒');
      logger.log('  player.rewind(10) - 快退 10 秒');
      logger.log('  player.next() - 下一集');
      logger.log('  player.previous() - 上一集');
      logger.log('  await player.setQuality("720P") - 切换清晰度 (返回操作结果)');
      logger.log('  await player.setSpeed("1.5x") - 切换倍速 (返回操作结果)');
      logger.log('  await player.setSubtitleEnabled(true) - 开关字幕 (返回操作结果)');
      logger.log('  await player.setSubtitleLanguage("zh") - 切换字幕语言 (返回操作结果)');
      logger.log(
        '  await player.setSubtitleSize("100%") - 设置字幕大小 (返回操作结果，支持: 60%, 80%, 100%, 120%, 130%)'
      );
      logger.log('  await player.setAudioLanguage("en") - 切换音频语言 (返回操作结果)');
      logger.log('  player.setSkipIntro(skipEnd, skipHeadBegin, skipStart, skipTailEnd) - 设置跳过片头片尾时间');
      logger.log('  player.getState() - 获取完整状态');
      logger.log('  player.getPlayerKernel() - 获取播放器内核（hls.js/origin）');
      logger.log('  player.setPlayerKernel("hls.js") - 设置播放器内核');
      logger.log('  player.instance - 访问原始播放器实例（如果存在）');
      logger.log('  playerStore.getState() - 直接访问 Zustand store');
      logger.log('%c💡 提示: 支持 async/await 的方法会返回 { success, message?, error? }', 'color: #ffa500;');
    }
  }
}

export default playerController;
